import React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { cn } from '@cotera/client/v0/lib/utils';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import type { DatasetPreviewHeaderContext } from './preview';
import type { DatasetAutomationsModel } from './dataset-automations.model';
import {
  STATE_PRESENTATION,
  loadAutomationWorkflowTallies,
  needsRunWhereSql,
  runnableWorkflows,
  type AutomationWorkflowTally,
  type DatasetAutomationWorkflow,
} from './dataset-automations';
import { Icon } from '../../components/ui/icon';
import {
  DatasetAddAutomationButton,
  useCanAddAutomation,
} from './dataset-add-automation';
import {
  DATASET_RUN_BASES,
  DATASET_RUN_BASIS_LABEL,
  datasetRunBasisNote,
  datasetRunBasisState,
  type DatasetRunBasis,
} from './dataset-run-scope';
import { RetryFailedDatasetRunsAction } from './retry-failed-dataset-runs.action';

/**
 * The four-bar glyph from the Workflows column, reused so a workflow reads the
 * same here as it does in the grid.
 */
const TallyBars: React.FC<{ tally: AutomationWorkflowTally | undefined }> = ({
  tally,
}) => {
  const segments: { key: string; count: number; dot: string }[] = [
    {
      key: 'succeeded',
      count: tally?.succeeded ?? 0,
      dot: STATE_PRESENTATION.succeeded.dot,
    },
    {
      key: 'running',
      count: tally?.running ?? 0,
      dot: STATE_PRESENTATION.running.dot,
    },
    {
      key: 'failed',
      count: tally?.failing ?? 0,
      dot: STATE_PRESENTATION.failed.dot,
    },
    {
      key: 'never',
      count: tally?.needsRun ?? 0,
      dot: STATE_PRESENTATION.never.dot,
    },
  ];
  const total = segments.reduce((sum, segment) => sum + segment.count, 0);
  return (
    <span className="flex items-center gap-px" aria-hidden>
      {segments.map((segment) => (
        <span
          key={segment.key}
          className={cn(
            'h-3 w-1 rounded-[1px]',
            // Zero still draws, faintly: a fixed four-bar shape is what makes
            // two workflows comparable at a glance.
            segment.count === 0 ? 'bg-muted' : segment.dot
          )}
          style={{
            opacity: total === 0 ? 0.4 : undefined,
          }}
        />
      ))}
    </span>
  );
};

/** The one-line summary under a workflow's name. */
const workflowSubtitle = (
  workflow: DatasetAutomationWorkflow,
  tally: AutomationWorkflowTally | undefined
): string => {
  const parts: string[] = [];
  // First, because it changes what the rest of the line means: a workflow that
  // only runs from here has not been quietly keeping up in the background, so
  // its "needs a run" count is the whole backlog rather than a lag.
  if (workflow.runMode === 'manual') {
    parts.push('manual only');
  }
  const writes = workflow.writes ?? [];
  parts.push(
    writes.length === 0 ? 'writes nothing back' : `writes ${writes.join(', ')}`
  );
  if (tally !== undefined && tally.failing > 0) {
    parts.push(`${tally.failing} failing`);
  }
  if (tally !== undefined && tally.needsRun > 0) {
    parts.push(`${tally.needsRun} need a run`);
  }
  return parts.join(' · ');
};

/**
 * The Run control in the automations column's header: a single button that
 * opens the menu, and a menu that says what a run would cover before starting
 * it.
 *
 * There is no button that runs straight away. The control sits one click from
 * dispatching thousands of workflow runs, and the scope it would use — every
 * row, or only the filtered ones, and which workflows — is exactly the thing
 * the user cannot see from the header. The menu shows that, and the run starts
 * from inside it against a number the user has read.
 *
 * All of its state is local. It deliberately does *not* live in the automations
 * overlay — that object's identity rebuilds the grid's whole view model, so a
 * checkbox toggling in here would reset the user's sorts, filters and expansion.
 * The one thing that does belong to the model is the run itself, which has to
 * outlive this menu closing.
 */
export const DatasetRunAllButton: React.FC<{
  automations: DatasetAutomationsModel;
  header: DatasetPreviewHeaderContext;
  /** Total rows in the artifact, ignoring filters. */
  totalRows: number | null;
  /** `null` until the dataset has loaded; the add-automation button needs it. */
  datasetId: string | null;
  datasetName: string;
}> = ({ automations, header, totalRows, datasetId, datasetName }) => {
  const boundWorkflows = useWatchableValue(automations.workflowsWatchable);
  const ownerOrgName = useWatchableValue(automations.ownerOrgNameWatchable);
  /**
   * The automations this menu can act on.
   *
   * Everything here starts runs, so it works from the runnable subset rather
   * than from everything bound to the dataset. On a dataset shared out of
   * another org those differ: its automations show in the column, with their
   * run state, but firing them is refused server-side — so a scope selector, a
   * row count and a Start for them would all be controls for a run that cannot
   * happen. Unshared, the two lists are the same list.
   */
  const workflows = React.useMemo(
    () => runnableWorkflows(boundWorkflows),
    [boundWorkflows]
  );
  const progress = useWatchableValue(automations.runProgressWatchable);
  const [open, setOpen] = React.useState(false);
  const [starting, setStarting] = React.useState(false);
  // Everything, until the user says otherwise. Re-seeded when the bound set
  // changes so a newly bound workflow is included rather than silently omitted.
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(
    () => new Set(workflows.map((workflow) => workflow.id))
  );
  React.useEffect(() => {
    setSelected(new Set(workflows.map((workflow) => workflow.id)));
  }, [workflows]);

  /**
   * Loaded when the menu opens rather than kept live.
   *
   * These counts only matter while the menu is showing them, and they are an
   * aggregate over every row — running it on each broadcast during a fan-out
   * would re-scan the dataset once per row that finishes.
   */
  const [tallies, setTallies] = React.useState<
    readonly AutomationWorkflowTally[]
  >([]);
  React.useEffect(() => {
    if (!open || workflows.length === 0) {
      return;
    }
    let cancelled = false;
    void loadAutomationWorkflowTallies(
      (selectSql) => header.aggregate({ scope: 'all', selectSql }),
      { workflows }
    ).then((loaded) => {
      if (!cancelled) {
        setTallies(loaded);
      }
    });
    return () => {
      cancelled = true;
    };
    // `header` is rebuilt on every grid render; the query it runs depends only
    // on the workflows and on the menu being open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workflows]);

  const tallyFor = React.useMemo(
    () => new Map(tallies.map((tally) => [tally.workflowId, tally])),
    [tallies]
  );

  const shownRows = header.shownRows ?? 0;
  /**
   * Which rows the run covers. A choice, but one that follows the grid:
   * filtering a column moves it there, because filtering is itself an act of
   * saying "these ones". Picking a chip afterwards sticks.
   */
  const [basis, setBasis] = React.useState<DatasetRunBasis>('all');
  React.useEffect(() => {
    if (header.hasFilters) {
      setBasis('filtered');
    }
  }, [header.hasFilters]);

  /**
   * Independent of the rows, which is the point: this used to be a fourth chip
   * in the same group, so "the rows I ticked, minus the ones already run" could
   * not be said at all.
   */
  const [onlyNeedsRun, setOnlyNeedsRun] = React.useState(false);

  // A large warehouse dataset ships the browser a sample. The cursor and
  // aggregates here only see that sample; an "all rows" run is handed to the
  // server, which covers the rest.
  const { rowCount } = header;
  const sampled =
    rowCount !== null &&
    rowCount.artifactRows !== null &&
    rowCount.totalRows !== null &&
    rowCount.totalRows > rowCount.artifactRows;

  const basisRows: Record<DatasetRunBasis, number> = {
    all: rowCount?.totalRows ?? totalRows ?? shownRows,
    filtered: shownRows,
  };
  const stateOf = (candidate: DatasetRunBasis) =>
    datasetRunBasisState({
      basis: candidate,
      hasFilters: header.hasFilters,
      rows: basisRows[candidate],
    });
  const basisState: Record<
    DatasetRunBasis,
    ReturnType<typeof datasetRunBasisState>
  > = {
    all: stateOf('all'),
    filtered: stateOf('filtered'),
  };
  const basisNote = datasetRunBasisNote({
    basis,
    hasFilters: header.hasFilters,
  });

  // Unticking the last row, or clearing the filters, empties the basis that was
  // reading from it. Falling back rather than running against a predicate the
  // chips no longer show anything for.
  const basisAvailable = basisState[basis].available;
  React.useEffect(() => {
    if (!basisAvailable) {
      setBasis('all');
    }
  }, [basisAvailable]);

  /**
   * How many of the rows in scope still owe a run — the exact number, counted
   * over the same rows the run would cover rather than read off the per-workflow
   * tallies. Those are one count per workflow over the whole dataset, and a row
   * owed by two selected workflows is one row, not two.
   *
   * Null while it is being counted, which is what keeps the footer from
   * flashing a stale number as the selection moves.
   */
  const [needsRunRows, setNeedsRunRows] = React.useState<number | null>(null);
  const needsRunSql =
    selected.size === 0 ? null : needsRunWhereSql(workflows, selected);
  React.useEffect(() => {
    if (!open || needsRunSql === null) {
      setNeedsRunRows(null);
      return;
    }
    let cancelled = false;
    void header
      .aggregate({
        scope: basis === 'filtered' ? 'filtered' : 'all',
        selectSql: `count(*) FILTER (WHERE ${needsRunSql}) AS needs_run`,
      })
      .then((values) => {
        if (!cancelled) {
          setNeedsRunRows(Number(values['needs_run'] ?? 0));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNeedsRunRows(null);
        }
      });
    return () => {
      cancelled = true;
    };
    // `header` is rebuilt on every grid render; the query depends on the rows
    // in scope and the workflows picked, both of which are listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, needsRunSql, basis]);

  // What Start will actually cover. Falls back to the whole row set while the
  // count is in flight, so the button never reads as running nothing.
  const rowsInBasis = basisState[basis].count ?? 0;
  const rowsToRun = onlyNeedsRun ? needsRunRows ?? rowsInBasis : rowsInBasis;
  // Counted over the sample, so rows outside it that still need a run are
  // missing from the number — shown as a floor, and never allowed to read as
  // "nothing to run".
  const countIsFloor = sampled && basis === 'all';
  const runIsFloor = countIsFloor && onlyNeedsRun;
  const runNumber = (count: number) =>
    `${count.toLocaleString()}${runIsFloor ? '+' : ''}`;

  // Read here rather than only inside the button so the empty state can say
  // why there is no button, instead of trailing off after the description.
  const canAddAutomation = useCanAddAutomation();

  const runCount = rowsToRun * selected.size;
  // Only while *this tab* is still sending batches. A replay is dispatched by
  // the server, so it never blocks the button that started it.
  const dispatching = progress?.dispatching === true;
  // A replay has no local loop and no count, but it *is* running in the
  // background until it drains or is stopped. Treat both as "a run is active"
  // for the pulse and the Start lockout, so the control reads as busy either
  // way rather than only while this tab happens to be looping.
  const replaying = progress?.kind === 'replay';
  // A batch this tab did not start — a reload of its own run, or a colleague's.
  // It is still running, so the controls must read as busy, but nothing local
  // is driving it.
  const adopted = progress?.adopted === true;
  const active = dispatching || replaying || adopted;

  const start = async () => {
    setStarting(true);
    setOpen(false);
    try {
      const workflowIds = [...selected];
      // The whole dataset is handed to the server rather than enumerated here:
      // it dispatches with crash-safety and a cap this tab cannot offer,
      // closing the tab does not strand it half-done, and it reaches rows a
      // sampled dataset never sent this tab. "Only rows that still need a run"
      // goes along as a flag, since the server can judge that per row. Filters
      // and ticked rows are only defined here, so those go row by row.
      if (basis === 'all') {
        await automations.replayAll({ workflowIds, onlyNeedsRun });
        return;
      }
      const cursor = await header.openRowIdCursor({
        scope: basis === 'filtered' ? 'filtered' : 'all',
        // Composed rather than chosen between: "the rows I ticked that still
        // need a run" is a predicate on each axis, and the query layer ANDs
        // them.
        ...(onlyNeedsRun && {
          extraWhereSql: needsRunWhereSql(workflows, selected),
        }),
      });
      try {
        await automations.runRows({ rows: cursor, workflowIds });
      } finally {
        await cursor.close();
      }
    } finally {
      setStarting(false);
    }
  };

  // Failed (row × workflow) pairs across the selected workflows — what a retry
  // would cover. Read from the same tallies the list shows, so the button's
  // count matches the "N failing" subtitles above it.
  const failingRows = React.useMemo(
    () =>
      workflows
        .filter((workflow) => selected.has(workflow.id))
        .reduce(
          (sum, workflow) => sum + (tallyFor.get(workflow.id)?.failing ?? 0),
          0
        ),
    [workflows, selected, tallyFor]
  );
  const retryFailedAction = useAction(RetryFailedDatasetRunsAction);
  const [retrying, setRetrying] = React.useState(false);
  const retryFailed = async () => {
    if (datasetId === null) {
      return;
    }
    setRetrying(true);
    setOpen(false);
    try {
      await retryFailedAction.execute({
        datasetId,
        workflowIds: [...selected],
        count: failingRows,
      });
    } finally {
      setRetrying(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {/* Never disabled while a run is going: the menu is also where the
            run's scope is read back, and a header control that vanishes into a
            disabled state mid-run leaves nothing to look at. */}
        <Button
          type="button"
          size="sm"
          variant="default"
          className="h-6 gap-0.5 px-1.5"
          aria-label={
            active ? 'Running — choose what to run' : 'Choose what to run'
          }
        >
          <Icon icon="zap" className={cn(active && 'animate-pulse')} />
          <ChevronDown className="h-3 w-3 opacity-80" />
        </Button>
      </DropdownMenuTrigger>
      {/* Sized to its contents rather than to a fixed width: the scope row
          carries its labels plus their counts, and a six-figure row count
          pushed the last one off the edge at a fixed 20rem. Bounded so a long
          workflow name cannot stretch it past the viewport.

          Aligned to the start because the column this sits in is pinned to the
          left edge of the grid, so a menu hung off the trigger's right would
          open past the window. */}
      <DropdownMenuContent
        align="start"
        side="bottom"
        className="w-max min-w-[24rem] max-w-[min(34rem,calc(100vw-2rem))] p-0"
      >
        {/* Nothing bound: the whole card is the invitation to bind something.
            A scope selector, a run count and a disabled Start are all controls
            for a run that cannot happen, and reading them is how a first-time
            user spends their attention working out that the answer is "none of
            these" — so none of them are drawn until there is a workflow for
            them to act on. */}
        {workflows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-6 text-center">
            {/* "None you can run" is a different state from "none at all", and
                telling a reader of a shared dataset that it has no automations
                while the column beside them is full of runs is worse than
                saying nothing. Either way the offer below is the same: build
                your own, which lands in your org and is yours to start. */}
            <span className="text-sm font-medium">
              {boundWorkflows.length === 0
                ? 'No automations yet'
                : 'Nothing here for you to run'}
            </span>
            <span className="max-w-[18rem] text-[11px] leading-snug text-muted-foreground">
              {boundWorkflows.length === 0
                ? 'An automation runs a workflow for every row of this dataset — as rows arrive, or on demand from here.'
                : `This dataset’s automations belong to ${
                    ownerOrgName ?? 'the org that shared it'
                  }, so they run there. You can still add one of your own.`}
            </span>
            {datasetId === null ? null : canAddAutomation ? (
              <span className="mt-1">
                <DatasetAddAutomationButton
                  datasetId={datasetId}
                  datasetName={datasetName}
                  // The draft opens in the artifact panel behind this menu, so
                  // leaving it up would cover the thing the click just opened.
                  onAdded={() => setOpen(false)}
                />
              </span>
            ) : (
              // Building one needs the artifact panel to open the draft in, and
              // this dataset is on its own page. Said here rather than left to
              // a filled button that can only answer with an error toast.
              <span className="mt-1 max-w-[18rem] text-[11px] leading-snug text-muted-foreground">
                Open this dataset from a chat to add one.
              </span>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-2 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Run on
              </div>
              {/* Three chips, one question — which rows.
                  What made the old row unreadable was not the chips: it was a
                  fourth chip asking a different question, three counts that are
                  the same number on an unfiltered dataset, and a chip that
                  disappeared entirely until the grid was filtered, so the card
                  looked like it could not narrow a run at all. Each chip now
                  says what it is waiting on instead of showing a dead zero, and
                  the line beneath says what the picked one covers. */}
              <div className="flex flex-wrap items-center gap-1">
                {DATASET_RUN_BASES.map((candidate) => {
                  const state = basisState[candidate];
                  const picked = basis === candidate;
                  return (
                    <button
                      key={candidate}
                      type="button"
                      aria-pressed={picked}
                      disabled={!state.available && !picked}
                      className={cn(
                        'flex flex-1 items-center justify-center gap-1.5 text-nowrap rounded border px-2 py-1.5 text-xs',
                        // The selection treatment the toggle group already
                        // uses, rather than the `foreground`/`background` pair
                        // these were set in: those are the text and page
                        // tokens, not control tokens, so in dark mode the
                        // picked chip came out a near-white slab brighter than
                        // any real button. Keeping it on the accent scale also
                        // leaves Start as the one filled control in the card.
                        picked
                          ? 'border-accent-foreground/25 bg-accent font-medium text-accent-foreground'
                          : 'border-border hover:bg-muted disabled:hover:bg-transparent',
                        // An unavailable chip is dimmed but still legible: the
                        // words in it are the instruction for making it usable,
                        // which is the one thing worth reading there.
                        !state.available && !picked && 'opacity-70'
                      )}
                      onClick={() => setBasis(candidate)}
                    >
                      {DATASET_RUN_BASIS_LABEL[candidate]}
                      <span
                        className={cn(
                          'text-[11px]',
                          state.count === null
                            ? 'italic text-muted-foreground'
                            : 'tabular-nums opacity-70'
                        )}
                      >
                        {state.count === null
                          ? state.requirement
                          : state.count.toLocaleString()}
                      </span>
                    </button>
                  );
                })}
              </div>
              {basisNote === null ? null : (
                <p className="text-[11px] leading-snug text-muted-foreground">
                  {basisNote}
                </p>
              )}
              {/* Its own line rather than a fourth row set: it narrows whatever
                  the rows above are, so it composes with a selection and with
                  the filters instead of replacing them. */}
              <label className="flex cursor-pointer items-start gap-2.5 pt-0.5">
                <Checkbox
                  className="mt-0.5"
                  checked={onlyNeedsRun}
                  disabled={selected.size === 0}
                  onCheckedChange={(checked) =>
                    setOnlyNeedsRun(checked === true)
                  }
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="text-xs">
                      Only rows that still need a run
                    </span>
                    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                      {selected.size === 0
                        ? '—'
                        : needsRunRows === null
                        ? '…'
                        : `${needsRunRows.toLocaleString()}${
                            countIsFloor ? '+' : ''
                          }`}
                    </span>
                  </span>
                  {/* What "needs a run" covers, which the label cannot say. */}
                  <span className="block text-[11px] leading-snug text-muted-foreground">
                    Never run, out of date, or cancelled.
                  </span>
                </span>
              </label>
            </div>

            <div className="border-t border-border px-3 py-2.5">
              {/* Select-all as a checkbox in the list's own gutter rather than
                  a "Clear all" / "Select all" text button off to the right:
                  it lines up with the checkboxes it controls, says which state
                  it is in without being read, and is one fewer button in a card
                  that had four of them competing with Start. */}
              <label className="flex cursor-pointer items-center gap-2.5 px-1 pb-1">
                <Checkbox
                  aria-label="Select every workflow"
                  checked={
                    selected.size === workflows.length
                      ? true
                      : selected.size === 0
                      ? false
                      : 'indeterminate'
                  }
                  onCheckedChange={() =>
                    setSelected(
                      selected.size === workflows.length
                        ? new Set()
                        : new Set(workflows.map((workflow) => workflow.id))
                    )
                  }
                />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {selected.size === workflows.length
                    ? `All ${workflows.length} workflows`
                    : `${selected.size} of ${workflows.length} workflows`}
                </span>
              </label>
              <div className="max-h-64 space-y-0.5 overflow-y-auto">
                {workflows.map((workflow) => {
                  const tally = tallyFor.get(workflow.id);
                  return (
                    <label
                      key={workflow.id}
                      className="flex cursor-pointer items-center gap-2.5 rounded px-1 py-1.5 hover:bg-muted/60"
                    >
                      <Checkbox
                        checked={selected.has(workflow.id)}
                        onCheckedChange={() =>
                          setSelected((current) => {
                            const next = new Set(current);
                            if (!next.delete(workflow.id)) {
                              next.add(workflow.id);
                            }
                            return next;
                          })
                        }
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">
                          {workflow.name}
                        </span>
                        {/* Wraps rather than truncates: the counts sit at the
                            end of this line, and they are the part worth
                            reading. */}
                        <span className="block text-[11px] leading-snug text-muted-foreground">
                          {workflowSubtitle(workflow, tally)}
                        </span>
                      </span>
                      <TallyBars tally={tally} />
                    </label>
                  );
                })}
              </div>
              {/* At the end of the list it belongs to, and quiet: adding an
                  automation is what you do *after* reading what is already
                  bound, and as a filled button at the top it competed for the
                  eye with the one button that dispatches thousands of runs. */}
              {datasetId !== null ? (
                <DatasetAddAutomationButton
                  datasetId={datasetId}
                  datasetName={datasetName}
                  variant="row"
                  onAdded={() => setOpen(false)}
                />
              ) : null}
            </div>

            <div className="flex items-center gap-3 border-t border-border bg-muted/30 px-3 py-2.5">
              <span className="min-w-0 flex-1 text-[11px] leading-snug text-muted-foreground">
                {dispatching ? (
                  <>
                    Queued{' '}
                    <span className="font-medium text-foreground tabular-nums">
                      {progress.dispatched.toLocaleString()}
                    </span>{' '}
                    of {progress.total.toLocaleString()}
                    {progress.finished > 0 ? (
                      <>
                        {' · '}
                        <span className="font-medium text-foreground tabular-nums">
                          {progress.finished.toLocaleString()}
                        </span>{' '}
                        done
                      </>
                    ) : null}
                  </>
                ) : progress !== null ? (
                  // Still going, but nothing local is driving it: either a
                  // replay the evaluator owns, or a batch adopted on load. The
                  // server counts it either way, so this reports how far it has
                  // got rather than merely that it is running — which is the
                  // whole point of the batch being a record rather than a
                  // variable in one tab.
                  //
                  // While the batch is still being dispatched its denominator
                  // can grow, so the percentage is withheld rather than shown
                  // running backwards.
                  <>
                    {progress.estimated ? (
                      <>
                        Running in the background —{' '}
                        <span className="font-medium text-foreground tabular-nums">
                          {progress.finished.toLocaleString()}
                        </span>{' '}
                        {progress.finished === 1 ? 'run' : 'runs'} finished so
                        far
                      </>
                    ) : (
                      <>
                        <span className="font-medium text-foreground tabular-nums">
                          {progress.percentComplete}%
                        </span>{' '}
                        complete —{' '}
                        <span className="font-medium text-foreground tabular-nums">
                          {progress.finished.toLocaleString()}
                        </span>{' '}
                        of {progress.runs.toLocaleString()}{' '}
                        {progress.runs === 1 ? 'run' : 'runs'}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {selected.size.toLocaleString()}{' '}
                    {selected.size === 1 ? 'workflow' : 'workflows'} ×{' '}
                    {runNumber(rowsToRun)}{' '}
                    {rowsToRun === 1 && !runIsFloor ? 'row' : 'rows'} ={' '}
                    <span className="font-medium text-foreground tabular-nums">
                      {runNumber(runCount)}
                    </span>{' '}
                    {runCount === 1 && !runIsFloor ? 'run' : 'runs'}
                  </>
                )}
              </span>
              {/* A separate action from Start: Start fires fresh runs over the
                  chosen rows; this retries the failed ones in place as new
                  attempts, so a fix to a workflow is replayed against the inputs
                  that failed. Shown only when the selection has failures. */}
              {failingRows > 0 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 shrink-0 whitespace-nowrap"
                  disabled={
                    active || starting || retrying || selected.size === 0
                  }
                  onClick={() => void retryFailed()}
                  title="Retry every failed run for the selected workflows"
                >
                  {retrying
                    ? 'Retrying…'
                    : `Retry ${failingRows.toLocaleString()} failed`}
                </Button>
              ) : null}
              {/* Sits next to Start: Stop is the counterpart to the run that is
                  going, so it reads as paired with it rather than adrift beside
                  the other controls. Offered for as long as a batch this tab
                  started has a group id, not only while the loop is going: the
                  runs it queued outlive the dispatch, and a replay has no local
                  loop at all — cancelling server-side is the only way to stop
                  either. */}
              {progress !== null ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 shrink-0 whitespace-nowrap"
                  onClick={() => void automations.stopRun()}
                  title="Stop dispatching and cancel the runs already queued"
                >
                  Stop
                </Button>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="default"
                className="h-7 shrink-0 whitespace-nowrap"
                disabled={
                  (runCount === 0 && !runIsFloor) ||
                  active ||
                  starting ||
                  retrying
                }
                onClick={() => void start()}
              >
                {active
                  ? 'Running…'
                  : `Start ${runNumber(runCount)} ${
                      runCount === 1 && !runIsFloor ? 'run' : 'runs'
                    }`}
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
